// src/components/workAccountability/waFormat.ts
//
// Pure display helpers for the Work Accountability UX (PR-E). Labels only — never
// re-derives policy logic (the resolver's `explanations` are the source of truth
// for "why"). Kept framework-free so it is trivially unit-testable.

import type {
  AccountabilityPolicy,
  AudienceTarget,
  RequirementLevel,
  TimeTrackingMode,
  WorkAccountabilityRules,
  WorkScopeTarget,
} from '../../types/workAccountability';

export const SYSTEM_DEFAULT_RULES: WorkAccountabilityRules = {
  timeTracking: { mode: 'disabled' },
  trackingMethod: { allowed: ['manual', 'completion'] },
  completionNote: 'disabled',
  proofOfDone: 'disabled',
  estimate: { required: false },
  variance: { explanationRequired: false, thresholdPct: 1000 },
  requireTimeBeforeCompletion: false,
  editReasonRequired: false,
  allowBackdatedEntry: true,
  approval: { required: false, approverType: 'none' },
};

export const TIME_TRACKING_MODES: TimeTrackingMode[] = ['disabled', 'optional', 'prompted', 'required'];
export const REQUIREMENT_LEVELS: RequirementLevel[] = ['disabled', 'optional', 'required'];

export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatMinutes(mins: number | null | undefined): string {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Variance vs the baseline estimate, as a signed percentage (floor). */
export function variancePct(actualMinutes: number, estimatedMinutes: number | null): number | null {
  if (estimatedMinutes == null || estimatedMinutes <= 0) return null;
  return Math.floor(((actualMinutes - estimatedMinutes) / estimatedMinutes) * 100);
}

/** Human audience summary (role targets are backend-reserved and never shown). */
export function audienceSummary(audience: AudienceTarget[], nameFor?: (id: string, kind: 'user' | 'circle') => string): string {
  if (!audience || audience.length === 0) return 'No audience';
  const includes = audience.filter((a) => a.mode === 'include');
  const excludes = audience.filter((a) => a.mode === 'exclude');
  const label = (t: AudienceTarget): string => {
    if (t.targetType === 'everyone') return 'Everyone';
    if (t.targetType === 'user') return nameFor?.(t.targetId ?? '', 'user') ?? 'A person';
    if (t.targetType === 'circle') return nameFor?.(t.targetId ?? '', 'circle') ?? 'A circle';
    return 'Role';
  };
  const inc = includes.map(label).join(', ') || 'No one';
  const exc = excludes.length ? ` (except ${excludes.map(label).join(', ')})` : '';
  return `${inc}${exc}`;
}

export function workScopeSummary(scope: WorkScopeTarget[], nameFor?: (id: string, kind: 'project' | 'user') => string): string {
  if (!scope || scope.length === 0) return 'No scope';
  return scope
    .map((s) => {
      if (s.scopeType === 'all') return 'All tasks';
      if (s.scopeType === 'project') return `Project: ${nameFor?.(s.scopeId ?? '', 'project') ?? 'a project'}`;
      return `Assigned to: ${nameFor?.(s.scopeId ?? '', 'user') ?? 'a person'}`;
    })
    .join(' · ');
}

/** A short, human list of the requirements a rule set imposes above the defaults. */
export function requirementsSummary(rules: WorkAccountabilityRules): string[] {
  const out: string[] = [];
  if (rules.timeTracking.mode !== 'disabled') out.push(`Time tracking: ${rules.timeTracking.mode}`);
  if (rules.requireTimeBeforeCompletion) out.push('Requires time before completion');
  if (rules.completionNote !== 'disabled') out.push(`Completion note: ${rules.completionNote}`);
  if (rules.proofOfDone !== 'disabled') out.push(`Proof of done: ${rules.proofOfDone}`);
  if (rules.estimate.required) out.push('Requires estimate');
  if (rules.variance.explanationRequired) out.push(`Variance explanation over ${rules.variance.thresholdPct}%`);
  if (rules.editReasonRequired) out.push('Requires edit reason');
  if (!rules.allowBackdatedEntry) out.push('No backdated entries');
  return out.length ? out : ['No additional requirements'];
}

export function isWorkspaceDefault(policy: AccountabilityPolicy): boolean {
  const everyone = policy.audience.some((a) => a.mode === 'include' && a.targetType === 'everyone');
  const allScope = policy.workScope.some((s) => s.scopeType === 'all');
  return everyone && allScope;
}
